<template>
  <div :class="{ 'reservations-cart': true, 'is-row': !isBlock }">
    <div class="reservations-cart-heading">
      <div class="time-data">
        <span class="date">
          {{ $t('reservationsCart.betweenStart') }} <strong>{{ startDate }}</strong> <br>{{ $t('reservationsCart.betweenMiddle') }}&nbsp;<strong>{{ endDate }}</strong>
        </span>
      </div>

      <icon
        v-if="isBlock"
        size="l"
        name="cart"
        primaryColor="color-text-light" />

      <div class="counter">
        {{ reservationsCount }}
      </div>
    </div>

    <div :class="{ 'contact-data': true, 'no-worker': true }">
      <strong class="client-name">
        {{ clientName }}
      </strong>

      <span
        v-if="peopleNumberEnabled"
        class="people-number">
        <icon
          name="profile-big"
          primaryColor="color-text-light"
          size="xs" />
        <span class="people-number-counter">{{ peopleNumber }}</span>
      </span>

      <a
        v-if="phone"
        href="#"
        :data-tel="phone"
        @click.prevent="$window.location.href='tel:' + phone;"
        class="phone">
        <icon
          name="phone"
          primaryColor="color-text-light"
          size="xs" />
        <span class="tooltip">
          {{ $t('common.ui.phonePrefix') }} {{ phone }}
        </span>
      </a>

      <a
        v-if="email"
        href="#"
        :data-email="email"
        :title="email"
        @click.prevent="$window.location.href='mailto:' + email;"
        class="email">
        <icon
          name="mailbox"
          primaryColor="color-text-light"
          size="xs" />
        <span class="tooltip">
          {{ $t('common.ui.emailPrefix') }} {{ email }}
        </span>
      </a>

      <span
        v-if="reservationsData[0].customer_id && userCanSeeContactData"
        class="client-info"
        @click="$bus.$emit('show-customer-popup', reservationsData[0].customer_id)">
        <icon
          name="info-circle-alt"
          primaryColor="color-text-light"
          strokeColor="color-text-light"
          size="xs" />
        <span class="tooltip">
          {{ $t('common.ui.clientInfo') }}
        </span>
      </span>
    </div>

    <div class="price">
      <span
        v-if="paymentsEnabled && hasPrice && userCanSeeFullPaymentsData"
        class="total-price">
        {{ $t('reservationsCart.totalCost') }} {{ totalPrice }}
      </span>
    </div>

    <div class="actions">
      <btn
        type="full-width with-icon"
        :title="$t('reservationsCart.seeCart')"
        iconName="cart"
        iconColor="color-text-light"
        @click.native="showPopup">
        {{ $t('reservationsCart.seeCart') }}
      </btn>
    </div>
  </div>
</template>

<script>
import Avatar from './../common/Avatar';
import CalendarUtils from './../helpers/CalendarUtils';
import Configuration from './../mixins/Configuration';
import CurrencyUtils from './../helpers/CurrencyUtils';
import Tooltip from './../common/Tooltip';
import ReservationStatusChange from './../mixins/ReservationStatusChange';
import UserTypes from './../mixins/UserTypes';
import WordVariations from './../mixins/WordVariations';

export default {
  name: 'reservation',
  mixins: [
    Configuration,
    ReservationStatusChange,
    UserTypes,
    WordVariations
  ],
  props: {
    isBlock: {
      type: Boolean,
      default: true
    },
    reservationsData: {
      type: [Object, Array]
    },
    cartID: {
      type: Number
    }
  },
  components: {
    'avatar': Avatar,
    'tooltip': Tooltip
  },
  computed: {
    startDate () {
      let dateFormat = 'D MMMM YYYY';
      let smallestTimestamp = 0;

      for (let i = 0; i < this.reservationsData.length; i++) {
        let time = CalendarUtils.convertDateToTimestamp(this.reservationsData[i].date);

        if (smallestTimestamp === 0 || time < smallestTimestamp) {
          smallestTimestamp = time;
        }
      }

      return this.$moment(smallestTimestamp).format(dateFormat);
    },
    endDate () {
      let dateFormat = 'D MMMM YYYY';
      let biggestTimestamp = 0;

      for (let i = 0; i < this.reservationsData.length; i++) {
        let time = CalendarUtils.convertDateToTimestamp(this.reservationsData[i].date);

        if (biggestTimestamp === 0 || time > biggestTimestamp) {
          biggestTimestamp = time;
        }
      }

      return this.$moment(biggestTimestamp).format(dateFormat);
    },
    clientName () {
      let clientName = this.reservationsData[0].user_name;
      return clientName;
    },
    peopleNumber () {
      let totalPeopleNumber = 0;

      for (let i = 0; i < this.reservationsData.length; i++) {
        totalPeopleNumber += this.reservationsData[i].people_number;
      }

      return totalPeopleNumber;
    },
    phone () {
      if (this.reservationsData[0].phone !== '') {
        return this.reservationsData[0].phone;
      }

      return false;
    },
    email () {
      if (this.reservationsData[0].confirm_email !== '') {
        return this.reservationsData[0].confirm_email;
      }

      return false;
    },
    hasPrice () {
      for (let i = 0; i < this.reservationsData.length; i++) {
        if (this.reservationsData[i].payment !== null) {
          return true;
        }
      }

      return false;
    },
    price () {
      let priceTotal = 0;

      for (let i = 0; i < this.reservationsData.length; i++) {
        if (this.reservationsData[i].payment !== null) {
          priceTotal += parseFloat(this.reservationsData[i].payment.price);
        }
      }

      if (priceTotal > 0) {
        let priceParts = (priceTotal).toString().split('.');

        if (priceParts[1] === '00') {
          return priceParts[0];
        }

        return Number(priceTotal).toFixed(2);
      }

      return false;
    },
    priceCurrency () {
      for (let i = 0; i < this.reservationsData.length; i++) {
        if (this.reservationsData[i].configuration_payment) {
          return this.reservationsData[i].configuration_payment.currency;
        }

        if (this.reservationsData[i].payment !== null) {
          return this.reservationsData[i].payment.currency;
        }
      }

      return this.$root.defaultCurrency;
    },
    totalPrice () {
      return CurrencyUtils.getQuote(this.price, this.priceCurrency);
    },
    reservationsCount () {
      return this.reservationsData.length + ' ' + this.reservationWordVariations(this.reservationsData.length);
    }
  },
  methods: {
    showPopup () {
      this.$bus.$emit('show-reservations-cart-popup', {
        inquiries: this.reservationsData,
        cartID: this.cartID
      });
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.reservations-cart {
  background: $color-light;
  border: 1px solid $color-border;
  border-radius: $border-radius-small;
  margin: 4rem 0 1rem;
  max-width: 25rem;
  padding: 2rem 2rem 6.75rem 2rem;
  position: relative;
  text-align: center;
  width: 100%;

  &:first-child {
    margin-left: 0;
  }

  &-heading {
    .icon {
      margin-top: 2rem;
      width: 100%!important;
    }
  }

  .time-data {
    font-size: 2.4rem;

    .date {
      color: $color-text-light;
      display: block;
      font-size: 1.3rem;
      margin-bottom: .5rem;
      width: 100%;
    }

    .time {
      width: 100%;
    }
  }

  .contact-data {
    padding: .5rem 0;

    .client-name {
      display: block;
      font-size: 1.3rem;
      margin-bottom: .5rem;
      padding: .5rem 0;
    }

    .people-number {
      &-counter {
        background: $color-text-light;
        border: 2px solid $color-light;
        border-radius: 1rem;
        color: $color-light;
        font-size: 1.2rem;
        font-weight: bold;
        margin-left: -.7rem;
        padding: .125rem .5rem;
        position: relative;
        top: -.4rem;
      }
    }

    .client-info {
      cursor: pointer;
    }

    .email,
    .phone,
    .client-info {
      padding: 0 .25rem;
      position: relative;

      &:hover {
        .tooltip {
          opacity: 1;
          top: -3rem;
        }
      }
    }

    .tooltip {
      background: $color-dark;
      border-radius: $border-radius-small;
      color: $color-light;
      display: block;
      font-size: 1.4rem;
      font-weight: 400;
      left: 50%;
      opacity: 0;
      padding: .5rem 1rem;
      pointer-events: none;
      position: absolute;
      top: -2.5rem;
      transform: translateX(-50%);
      transition: all .25s ease-out;
      white-space: nowrap;
      z-index: 1;

      &:after {
        border: .5rem solid $color-dark;
        border-left-color: transparent;
        border-right-color: transparent;
        border-bottom-color: transparent;
        bottom: -1rem;
        content: "";
        height: 1rem;
        left: 50%;
        position: absolute;
        transform: translateX(-50%);
        width: 1rem;
      }
    }
  }

  .counter {
    background: $color-notice;
    border-radius: .9rem;
    color: $color-light;
    display: inline-block;
    font-size: 1.2rem;
    font-weight: 600;
    letter-spacing: -.5px;
    line-height: 1.8rem;
    margin: 1rem auto 0 auto;
    padding: .25rem 1.5rem;
    width: auto;
  }

  .price {
    border-top: 1px solid $color-border;
    font-size: 1.3rem;
    margin: .5rem 0 0 0;

    .payment-status {
      .is-paid {
        color: $color-success;
      }

      .is-not-paid {
        color: $color-danger;
      }
    }

    .payment-status,
    .total-price {
      color: $color-text-light;
      display: inline-block;
      font-size: 1.4rem;
      letter-spacing: -.025rem;
      margin: 1.5rem 0 0 0;
    }

    .service {
      cursor: pointer;
      display: block;
      font-size: 1.3rem;
      font-weight: bold;
      padding: .75rem 0;
      position: relative;

      & ~ .payment-status,
      & ~ .total-price {
        font-size: 1.3rem;
        margin-top: 0;
      }

      &.no-price {
        margin-top: 1rem;
      }

      &:hover {
        .tooltip {
          opacity: 1;
          top: 50%;

          &.tooltip-top {
            top: -24px;
          }
        }
      }
    }
  }

  .actions {
    align-items: center;
    bottom: 2rem;
    display: flex;
    justify-content: center;
    left: 50%;
    margin: .5rem 0 0 0;
    padding: 1.5rem 0 0 0;
    position: absolute;
    transform: translateX(-50%);
    width: 80%;

    .button {
      margin: 0 .5rem;

      &:first-of-type {
        margin-left: 0;
      }
    }
  }

  &.is-row {
    align-items: center;
    border-radius: 0;
    display: flex;
    margin-bottom: -.1rem!important;
    margin-top: 0!important;
    max-width: 100%!important;
    padding: 1rem 3rem;
    text-align: left;
    width: 100%!important;

    .reservations-cart-heading {
      order: 3;
      width: 40%;
    }

    .contact-data {
      order: 1;
      padding-left: 6.5rem;
      width: 25%;

      &.no-worker {
        padding-left: 0;
      }
    }

    .counter {
      display: inline-block;
      margin: .5rem 1rem 0 0;
      padding: 0 .75rem;
      text-align: center;
      width: auto;
    }

    .time-data {
      display: flex;
      flex-wrap: wrap;
      font-size: 1.8rem;
    }

    .price {
      border-top: none;
      margin-top: 0;
      order: 3;
      width: 25%;

      .service,
      .service.no-price {
        margin-top: 0;
      }

      .total-price {
        margin: 0;
      }
    }

    .actions {
      align-items: center;
      display: flex;
      justify-content: center;
      margin-left: auto;
      margin-top: 0;
      order: 4;
      padding-top: 0;
      position: static;
      text-align: right;
      transform: none;
      width: 20rem;

      .button {
        margin: 0 .5rem;
        text-align: center;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/ReservationsCart.vue